<template>
  <div class="w-full overflow-hidden">
    <header
      id="header"
      class="relative text-center wrap pb-y-gutter-md z-3above md:text-left"
    >
      <div
        class="container flex flex-col flex-no-wrap md:flex-row 3xl:items-center"
      >
        <div class="flex-auto min-h-px md:w-2/3 md:pr-16 xl:pr-32">
          <!-- eslint-disable-next-line vue/no-v-html -->
          <h1 class="heading" v-html="introTitleHtml" />
          <sanity-blocks
            v-if="introText && introText.length > 0"
            :blocks="introText"
            class="heading--sm max-w-192"
          />
        </div>
        <div
          class="flex justify-center flex-initial order-first min-h-px mb-y-gutter-sm md:order-none md:mb-0 md:w-1/3"
        >
          <slot name="hero">
            <portal
              v-if="
                featuredProducts &&
                  featuredProducts.products &&
                  featuredProducts.products.length > 0
              "
              :product="featuredProducts.products[0]"
              class="w-1/2 md:w-full"
            />
          </slot>
        </div>
      </div>
    </header>
    <div id="content">
      <slot name="before-grid">
        <div class="relative z-2above">
          <gradient
            v-if="!hideGradients"
            :colors="[transparentColor, gridBackgroundColor]"
            force-gradient
            class="absolute bottom-0 left-0 w-full pointer-events-none h-header-height z-above"
          />
        </div>
      </slot>
      <product-grid
        :clouds-disabled="hasBackgroundImage"
        :style="{ backgroundColor: !hideGradients && gridBackgroundColor }"
        :products="filteredProducts"
        :marquees="marquees"
        :cell-background-color="getColor('apricot')"
        class="relative z-2above"
      />
      <div class="relative">
        <slot name="after-grid" />
        <page-section
          v-if="
            slug !== 'sparkling-water' &&
              featuredProducts &&
              (featuredProducts.text || featuredProducts.length > 0)
          "
          :title="featuredProducts.title"
          :cta="featuredProducts.cta"
          class="relative z-2above"
        >
          <template #header>
            <sanity-blocks
              v-if="featuredProducts.text"
              :blocks="featuredProducts.text"
            />
          </template>
          <products
            v-if="featuredProducts.products.length > 0"
            :products="featuredProducts.products"
            hover-images
            square
          />
        </page-section>
        <page-section
          v-if="outroText && outroText.length > 0"
          :title="outroTitle"
          :cta="outroCta"
          class="relative z-2above"
        >
          <div class="container text-center container--narrow">
            <sanity-blocks :blocks="outroText" class="heading--sm" />
          </div>
        </page-section>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import ProductGrid from './product-grid.vue'
import preventOrphanedWord from '~/util/text/prevent-orphaned-word'
import Portal from '~/components/portal.vue'
import Products from '~/components/shop/products/products.vue'
import { colors } from '~/recess.json'
import getCBDFilter from '~/util/get-cbd-filter'

export default {
  name: 'ProductLanding',

  components: {
    Portal,
    ProductGrid,
    Products
  },

  props: {
    title: {
      default: null,
      type: String
    },
    slug: {
      default: null,
      type: String
    },
    introTitle: {
      default: null,
      type: String
    },
    introText: {
      default: () => [],
      type: Array
    },
    introHero: {
      default: null,
      type: Object
    },
    products: {
      default: () => [],
      type: Array
    },
    marquees: {
      default: undefined,
      type: Array
    },
    featuredProducts: {
      default: undefined,
      type: Object
    },
    outroTitle: {
      default: undefined,
      type: String
    },
    outroText: {
      default: undefined,
      type: Array
    },
    outroCta: {
      default: undefined,
      type: Object
    },
    hideGradients: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      isCBDSafe: getCBDFilter(process.env.nonCBDSite)
    }
  },

  computed: {
    ...mapState('screen', {
      backgroundColors(state) {
        return state.pageColors.backgroundColors
      }
    }),
    ...mapState({
      googleClientId: state => state.googleClientId
    }),
    ...mapGetters('screen', {
      bp: 'breakpoint',
      transparentColor: 'transparentColor'
    }),
    filteredProducts() {
      return this.products.filter(this.isCBDSafe)
    },
    introTitleHtml() {
      const title = this.introTitle || this.title
      if (!title) {
        return
      }
      return preventOrphanedWord(title)
    },
    hasBackgroundImage() {
      return !this.backgroundImage
    },
    gridBackgroundColor() {
      return (
        this.backgroundColors[1] ?? this.backgroundColors[0] ?? colors.apricot
      )
    }
  },

  mounted() {
    this.$nextTick(this.trackView)
  },

  methods: {
    trackView() {
      const title = this.title
      const slug = this.slug
      const products = this.filteredProducts
      this.$gtm.push({
        event: 'Product List Viewed',
        google_client_id: this.googleClientId,
        list_id: slug,
        category: title,
        products: products.map((p, i) => {
          const shopifyProduct = p.shopify && p.shopify.product
          const data = {
            name: p.title,
            position: i + 1
          }
          if (shopifyProduct) {
            data.product_id = shopifyProduct.id
            data.sku = shopifyProduct.id
            data.price =
              shopifyProduct.priceRange &&
              shopifyProduct.priceRange.minVariantPrice.amount
            data.url = `${process.env.canonicalUrl}${this.documentPath(p)}`
          }
          return data
        })
      })
    }
  }
}
</script>
